import React, { useState } from 'react';
import { Stack, Text, Group, Box, Card, SimpleGrid, ScrollArea, ActionIcon, Modal, Button, Image, Center } from '@mantine/core';
import { ArrowLeft, Building2, Layers, Maximize2, MapPin, FileText, Download } from 'lucide-react';
import { DocumentItem } from '../components/DocumentItem';
import type { Property, Document } from '../types';
interface PropertyDetailsProps {
  propertyId?: string;
  onBack?: () => void;
}
// Mock data
const mockProperty: Property = {
  id: '1',
  name: 'Marina Heights - Unit 1204',
  unitId: '1204',
  floor: 12,
  area: 1250,
  areaUnit: 'sqft',
  projectName: 'Marina Heights',
  image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&auto=format&fit=crop',
  nextInstallment: {
    amount: 125000,
    dueDate: '2024-02-15'
  },
  constructionProgress: 68
};
const mockDocuments: Document[] = [{
  id: '1',
  name: 'Sales & Purchase Agreement',
  type: 'contract',
  url: '#',
  uploadedAt: '2023-06-15',
  size: '2.4 MB'
}, {
  id: '2',
  name: 'Expression of Interest',
  type: 'eoi',
  url: '#',
  uploadedAt: '2023-05-20',
  size: '1.1 MB'
}, {
  id: '3',
  name: 'Floor Plan - Unit 1204',
  type: 'floor-plan',
  url: 'https://images.unsplash.com/photo-1580820267682-426da823b514?w=800&auto=format&fit=crop',
  uploadedAt: '2023-06-15',
  size: '856 KB'
}, {
  id: '4',
  name: 'Payment Receipt - Booking',
  type: 'receipt',
  url: '#',
  uploadedAt: '2023-05-22',
  size: '245 KB'
}];
export function PropertyDetails({
  onBack
}: PropertyDetailsProps) {
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const specs = [{
    label: 'Unit ID',
    value: mockProperty.unitId,
    icon: Building2
  }, {
    label: 'Floor',
    value: `${mockProperty.floor}th Floor`,
    icon: Layers
  }, {
    label: 'Area',
    value: `${mockProperty.area.toLocaleString()} ${mockProperty.areaUnit}`,
    icon: Maximize2
  }, {
    label: 'Project',
    value: mockProperty.projectName,
    icon: MapPin
  }];
  const renderDocumentPreview = (doc: Document) => {
    // Check if it's an image based on type or extension (mock logic)
    const isImage = doc.type === 'floor-plan' || doc.url.match(/\.(jpeg|jpg|gif|png)$/) != null || doc.url.includes('images.unsplash.com');
    if (isImage) {
      return <Box data-id="element-411">
          <Image src={doc.url} alt={doc.name} radius="md" fit="contain" style={{
          maxHeight: '60vh'
        }} fallbackSrc="https://placehold.co/600x400?text=Image+Not+Found" data-id="element-412" />
        </Box>;
    }
    // PDF or other documents
    return <Stack align="center" py="xl" gap="lg" data-id="element-413">
        <Box p="xl" style={{
        backgroundColor: '#f8fafc',
        borderRadius: '50%'
      }} data-id="element-414">
          <FileText size={48} color="#64748b" data-id="element-415" />
        </Box>
        <Stack gap="xs" align="center" data-id="element-416">
          <Text size="lg" fw={600} c="#1a2332" data-id="element-417">
            {doc.name}
          </Text>
          <Text c="#64748b" ta="center" maw={400} data-id="element-418">
            This document cannot be previewed directly in the browser. Please
            download it to view the contents.
          </Text>
        </Stack>
        <Button leftSection={<Download size={18} data-id="element-420" />} variant="light" color="blue" onClick={() => console.log('Download:', doc.name)} data-id="element-419">
          Download Document ({doc.size})
        </Button>
      </Stack>;
  };
  return <>
      <ScrollArea h="100%" offsetScrollbars data-id="element-421">
        <Stack gap="lg" pb={100} data-id="element-422">
          {/* Header Image */}
          <Box style={{
          height: 220,
          background: `linear-gradient(to bottom, transparent 40%, rgba(26, 35, 50, 0.9)), url(${mockProperty.image}) center/cover`,
          borderRadius: '0 0 24px 24px',
          marginTop: -16,
          marginLeft: -16,
          marginRight: -16,
          position: 'relative'
        }} data-id="element-423">
            {/* Back Button */}
            <ActionIcon variant="filled" size="lg" radius="xl" onClick={onBack} style={{
            position: 'absolute',
            top: 16,
            left: 16,
            backgroundColor: 'rgba(255,255,255,0.9)'
          }} aria-label="Go back" data-id="element-424">
              <ArrowLeft size={20} color="#1a2332" data-id="element-425" />
            </ActionIcon>

            {/* Property Title */}
            <Box style={{
            position: 'absolute',
            bottom: 20,
            left: 20,
            right: 20
          }} data-id="element-426">
              <Text fw={700} size="xl" c="white" data-id="element-427">
                {mockProperty.name}
              </Text>
              <Text size="sm" c="rgba(255,255,255,0.8)" data-id="element-428">
                {mockProperty.projectName}
              </Text>
            </Box>
          </Box>

          {/* Unit Specifications */}
          <Stack gap="sm" data-id="element-429">
            <Text fw={700} size="lg" c="#1a2332" data-id="element-430">
              Unit Specifications
            </Text>
            <SimpleGrid cols={2} spacing="md" data-id="element-431">
              {specs.map(spec => {
              const Icon = spec.icon;
              return <Card key={spec.label} p="md" radius="md" withBorder styles={{
                root: {
                  borderColor: '#e2e8f0'
                }
              }} data-id="element-432">
                    <Group gap="sm" wrap="nowrap" data-id="element-433">
                      <Box p={8} style={{
                    backgroundColor: '#f1f5f9',
                    borderRadius: 8
                  }} data-id="element-434">
                        <Icon size={18} color="#64748b" data-id="element-435" />
                      </Box>
                      <Stack gap={2} data-id="element-436">
                        <Text size="xs" c="#64748b" tt="uppercase" fw={500} data-id="element-437">
                          {spec.label}
                        </Text>
                        <Text fw={600} size="sm" c="#1a2332" data-id="element-438">
                          {spec.value}
                        </Text>
                      </Stack>
                    </Group>
                  </Card>;
            })}
            </SimpleGrid>
          </Stack>

          {/* Documents Section */}
          <Stack gap="sm" data-id="element-439">
            <Group justify="space-between" align="center" data-id="element-440">
              <Text fw={700} size="lg" c="#1a2332" data-id="element-441">
                Documents
              </Text>
              <Text size="sm" c="#64748b" data-id="element-442">
                {mockDocuments.length} files
              </Text>
            </Group>
            <Stack gap="sm" data-id="element-443">
              {mockDocuments.map(doc => <DocumentItem key={doc.id} document={doc} onDownload={() => console.log('Download:', doc.name)} onView={() => setSelectedDocument(doc)} data-id="element-444" />)}
            </Stack>
          </Stack>
        </Stack>
      </ScrollArea>

      {/* Document Viewer Modal */}
      <Modal opened={!!selectedDocument} onClose={() => setSelectedDocument(null)} title={<Text fw={700} size="lg" data-id="element-446">
            {selectedDocument?.name}
          </Text>} size="xl" padding="lg" centered overlayProps={{
      backgroundOpacity: 0.55,
      blur: 3
    }} data-id="element-445">
        {selectedDocument && renderDocumentPreview(selectedDocument)}
      </Modal>
    </>;
}